import { memo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import { WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type { WhatsAppTemplate } from "../WhatsAppCampaignTypes";
import { formatTemplateName, getBodyVariableCount } from "../WhatsAppCampaignUtils";
import { TemplateStatusBadge } from "./WhatsAppMessageParts";

const SUMMARY_KEY = `${WHATSAPP_EDITOR_KEY}.selectedTemplate`;

interface SelectedTemplateSummaryProps {
  template: WhatsAppTemplate;
  onChangeTemplate: () => void;
}

const SelectedTemplateSummary: FC<SelectedTemplateSummaryProps> = ({
  template,
  onChangeTemplate,
}) => {
  const { t, i18n } = useTranslation();
  const bodyVariableCount = getBodyVariableCount(template);
  const buttonCount = template.buttons.length;
  const headerFormat = template.headerFormat?.toLowerCase();

  const structure = [
    headerFormat && t(`${SUMMARY_KEY}.structure.header.${headerFormat}`, { defaultValue: "" }),
    t(`${SUMMARY_KEY}.structure.body`, {
      count: bodyVariableCount,
      formattedCount: formatNumber(bodyVariableCount, {}, i18n.language),
    }),
    buttonCount > 0 &&
      t(`${SUMMARY_KEY}.structure.quickActions`, {
        count: buttonCount,
        formattedCount: formatNumber(buttonCount, {}, i18n.language),
      }),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <section className="wa-selected-template">
      <div className="wa-selected-template__main">
        <span className="wa-selected-template__label">{t(`${SUMMARY_KEY}.label`)}</span>
        <div className="wa-selected-template__heading">
          <h3 className="wa-selected-template__name">{formatTemplateName(template.name)}</h3>
          <TemplateStatusBadge status={template.status} />
        </div>
        <p className="wa-selected-template__meta">
          {t(`${SUMMARY_KEY}.templateId`)}{" "}
          <code className="wa-selected-template__code">{template.id}</code>
          {template.language && ` • ${t(`${SUMMARY_KEY}.locale`, { locale: template.language })}`}
          {` • ${t(`${SUMMARY_KEY}.structureLabel`, { structure })}`}
        </p>
      </div>
      <button type="button" className="wa-selected-template__change" onClick={onChangeTemplate}>
        {t(`${SUMMARY_KEY}.change`)}
      </button>
    </section>
  );
};

export default memo(SelectedTemplateSummary);
